import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiSave, FiX, FiUser, FiBriefcase, FiAlertCircle, FiPlus, FiMinus } from 'react-icons/fi';
import { useCliente } from '../../hooks/useClientes';
import ConfirmModal from '../../components/UI/Modal/ConfirmModal';
import CreateCategoryModal from '../../components/UI/CreateCategoryModal';
import { clientCategoriesService } from '../../services';


const ClienteForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const {
    cliente,
    isEditing,
    isLoading,
    error,
    hasChanges,
    updateClienteData,
    saveCliente,
    resetCliente,
    clearError
  } = useCliente(id);

  const [validationErrors, setValidationErrors] = useState({});
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingZipCode, setIsLoadingZipCode] = useState(false);
  const [showCreateCategoryModal, setShowCreateCategoryModal] = useState(false);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);

  // Cargar categorías desde el backend
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    setIsLoadingCategories(true);
    try {
      const response = await clientCategoriesService.getCategorias({ is_active: true });
      if (response.success) {
        const formattedCategories = response.data.categories.map(cat => ({
          id: cat.id,
          value: cat.code,
          label: cat.name
        }));
        setCategorias(formattedCategories);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Fallback a categorías por defecto si falla
      setCategorias([
        { id: null, value: 'DISTRIBUTOR', label: 'DISTRIBUIDOR' },
        { id: null, value: 'TIENDA', label: 'TIENDA' }
      ]);
    } finally {
      setIsLoadingCategories(false);
    }
  };

  const handleCreateCategory = async (categoryData) => {
    try {
      const response = await clientCategoriesService.crearCategoria(categoryData);
      if (response.success) {
        // Recargar categorías
        await loadCategories();
        // Seleccionar automáticamente la nueva categoría
        handleInputChange('clientCategory', response.data.code);
      }
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Error al crear la categoría. Por favor intente nuevamente.');
    }
  };

  const handleDeleteCategory = async () => {
    const selectedCategoryCode = cliente.clientCategory;

    if (!selectedCategoryCode) {
      alert('Por favor seleccione una categoría para eliminar');
      return;
    }

    // Buscar el ID de la categoría seleccionada
    const selectedCategory = categorias.find(c => c.value === selectedCategoryCode);

    if (!selectedCategory || !selectedCategory.id) {
      alert('No se encontró el ID de la categoría seleccionada');
      return;
    }

    try {
      const response = await clientCategoriesService.eliminarCategoria(selectedCategory.id);
      if (response.success) {
        // Recargar categorías
        await loadCategories();
        // Limpiar la selección
        handleInputChange('clientCategory', '');
        setShowDeleteCategoryModal(false);
      } else {
        alert(response.message || 'Error al eliminar la categoría');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Error al eliminar la categoría. Por favor intente nuevamente.');
    }
  };

  const tiposCliente = [
    { value: 'betel_natural', label: 'BETEL NATURAL' },
    { value: 'naturalandlab', label: 'NATURALANDLAB' },
    { value: 'both', label: 'AMBOS' }
  ];
  
  const tiposPersona = [
    { value: 'individual', label: 'PERSONA INDIVIDUAL' },
    { value: 'company', label: 'EMPRESA' }
  ];

  const tiposPago = [
    { value: 'COD', label: 'COD (Pago al instante)' },
    { value: 'TERMINOS', label: 'TÉRMINOS' }
  ];

  const vendedores = [
    { value: 1, label: 'VENDEDOR 1' },
    { value: 2, label: 'VENDEDOR 2' },
    { value: 3, label: 'VENDEDOR 3' },
    { value: 4, label: 'VENDEDOR 4' }
  ];

  const tiposEnvio = [
    { value: 'ups', label: 'UPS' },
    { value: 'pallet', label: 'PALET' },
    { value: 'driver_delivery', label: 'ENTREGA CHOFER' },
    { value: 'pickup_betel', label: 'RECOGER EN BETEL' }
  ];

  const paises = [
    { value: 'Estados Unidos', label: 'Estados Unidos' },
    { value: 'Canadá', label: 'Canadá' },
    { value: 'Puerto Rico', label: 'Puerto Rico' }
  ];

  const estados = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' }
  ];

  // Limpiar errores cuando se monta el componente
  useEffect(() => {
    clearError();
    setValidationErrors({});
  }, [clearError]);

  const handleInputChange = (field, value) => {
    updateClienteData(field, value);
    
    // Lógica especial para tipo de persona
    if (field === 'clientType') {
      if (value === 'individual') {
        // Si cambia a individual, limpiar campos de empresa
        updateClienteData('companyName', '');
        updateClienteData('taxId', '');
      }
    }
    
    // Lógica especial para tipo de pago
    if (field === 'paymentType') {
      if (value === 'TERMINOS') {
        // Si cambia a términos y no tiene límite de crédito, establecer un valor mínimo
        if (!cliente.creditLimit || cliente.creditLimit === 0) {
          updateClienteData('creditLimit', 1000);
        }
        // Asegurar que tenga días de crédito
        if (!cliente.paymentTerms || cliente.paymentTerms === 0) {
          updateClienteData('paymentTerms', 30);
        }
      } else if (value === 'COD') {
        // Si cambia a COD, limpiar campos de crédito
        updateClienteData('creditLimit', 0);
        updateClienteData('paymentTerms', 0);
      }
    }
    
    // Limpiar error de validación del campo específico
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Función para buscar información por código postal
  const handleZipCodeLookup = async (zipCode, addressType = 'business') => {
    if (!zipCode || zipCode.length < 5) return;

    setIsLoadingZipCode(true);

    try {
      // API gratuita de Zippopotam para USA
      const response = await fetch(`https://api.zippopotam.us/us/${zipCode}`);

      if (response.ok) {
        const data = await response.json();

        if (data.places && data.places.length > 0) {
          const place = data.places[0];

          // Autocompletar campos según el tipo de dirección
          if (addressType === 'business') {
            updateClienteData('businessCity', place['place name']);
            updateClienteData('businessState', place['state abbreviation']);
            updateClienteData('businessCountry', 'Estados Unidos');
          } else if (addressType === 'delivery') {
            updateClienteData('deliveryCity', place['place name']);
            updateClienteData('deliveryState', place['state abbreviation']);
            updateClienteData('deliveryCountry', 'Estados Unidos');
          }
        }
      }
    } catch (error) {
      console.error('Error al buscar código postal:', error);
    } finally {
      setIsLoadingZipCode(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setIsSaving(true);
    setValidationErrors({});
    
    try {
      const result = await saveCliente();

      console.log(result);
      if (result.success) {
        // Navegar de vuelta a la lista con mensaje de éxito
        navigate('/system/clientes', {
          state: {
            message: result.message,
            type: 'success'
          }
        });
      } else {
        console.log(result);
        
        // Procesar errores de validación
        const processedErrors = {};
        
        // Si hay validation_errors (array format)
        if (result.validation_errors && Array.isArray(result.validation_errors)) {
          result.validation_errors.forEach(err => {
            processedErrors[err.field] = err.message;
          });
        }
        
        // Si hay validationErrors (object format)
        if (result.validationErrors && typeof result.validationErrors === 'object') {
          Object.assign(processedErrors, result.validationErrors);
        }
        
        // Si no hay errores específicos pero hay error general
        if (Object.keys(processedErrors).length === 0 && result.error) {
          processedErrors.general = result.error;
        }
        
        setValidationErrors(processedErrors);
      }
    } catch (err) {
      setValidationErrors({ 
        general: 'Error inesperado al guardar el cliente' 
      });
      console.error('Unexpected error:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      setShowUnsavedChangesModal(true);
    } else {
      navigate('/system/clientes');
    }
  };

  const confirmCancel = () => {
    resetCliente();
    navigate('/system/clientes');
  };

  if (isLoading) {
    return (
      <div className="page-loading">
        <div className="loading-spinner-single" style={{ width: '40px', height: '40px' }}></div>
        <div className="loading-message">
          {isEditing ? 'Cargando cliente...' : 'Preparando formulario...'}
        </div>
      </div>
    );
  }

  return (
    <div className="cliente-form-page">
      {/* Header */}
      <div className="form-header">
        <button 
          className="btn btn--ghost"
          onClick={handleCancel}
          type="button"
        >
          <FiArrowLeft /> Volver a Clientes
        </button>
        
        <h1 className="form-title">
          {isEditing ? 'EDITAR CLIENTE' : 'NUEVO CLIENTE'}
        </h1>
        
        <div className="form-actions">
          <button 
            className="btn btn--outline"
            onClick={handleCancel}
            type="button"
            disabled={isSaving}
          >
            <FiX /> Cancelar
          </button>
          
          <button 
            className="btn btn--primary"
            onClick={handleSubmit}
            disabled={isSaving || !hasChanges}
            type="submit"
          >
            {isSaving ? (
              <>
                <div className="loading-spinner-single" style={{ width: '16px', height: '16px', marginRight: '8px' }}></div>
                Guardando...
              </>
            ) : (
              <>
                <FiSave /> {isEditing ? 'Actualizar' : 'Crear'} Cliente
              </>
            )}
          </button>
        </div>
      </div>

      {/* Errores generales */}
      {(error || validationErrors.general) && (
        <div className="alert alert--danger">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error || validationErrors.general}
          </div>
        </div>
      )}

      {/* Errores de validación específicos */}
      {Object.keys(validationErrors).length > 1 || (Object.keys(validationErrors).length === 1 && !validationErrors.general) ? (
        <div className="alert alert--warning">
          <FiAlertCircle className="alert-icon" />
          <div className="alert-content">
            <strong>Errores de validación:</strong>
            <ul className="error-list">
              {Object.entries(validationErrors)
                .filter(([field]) => field !== 'general')
                .map(([field, message]) => (
                  <li key={field}>{message}</li>
                ))
              }
            </ul>
          </div>
        </div>
      ) : null}

      {/* Formulario */}
      <form onSubmit={handleSubmit} className="cliente-form">
        <div className="form-columns">
          {/* Columna Izquierda */}
          <div className="form-column">
            {/* Sección: Información Básica */}
            <div className="form-section">
              <div className="section-header">
                <FiBriefcase className="section-icon" />
                <h2 className="section-title">Información Básica</h2>
              </div>

              {/* Campo oculto para establecer tipo empresa por defecto */}
              <input type="hidden" value="company" />

              <div className="form-group">
                <label className="form-label required">Nombre de Negocio</label>
                <input
                  type="text"
                  className={`form-input ${validationErrors.companyName ? 'error' : ''}`}
                  value={cliente.companyName || ''}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  placeholder="Ingrese el nombre del negocio"
                  maxLength={255}
                />
                {validationErrors.companyName && (
                  <span className="form-error">{validationErrors.companyName}</span>
                )}
              </div>
            </div>

            {/* Sección: Dirección del Negocio */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Dirección del Negocio</h3>
              </div>
              
              <div className="form-group">
                <label className="form-label">Número y Calle</label>
                <input
                  type="text"
                  className="form-input"
                  value={cliente.businessAddress || ''}
                  onChange={(e) => handleInputChange('businessAddress', e.target.value)}
                  placeholder="Número y calle"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Unidad (Apartamento, Suite, etc.)</label>
                <input
                  type="text"
                  className="form-input"
                  value={cliente.businessUnit || ''}
                  onChange={(e) => handleInputChange('businessUnit', e.target.value)}
                  placeholder="Apt, Suite, Unidad, etc. (opcional)"
                />
              </div>

              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Ciudad</label>
                  <input
                    type="text"
                    className="form-input"
                    value={cliente.businessCity || ''}
                    onChange={(e) => handleInputChange('businessCity', e.target.value)}
                    placeholder="Ciudad"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">Estado</label>
                  <select
                    className="form-input"
                    value={cliente.businessState || ''}
                    onChange={(e) => handleInputChange('businessState', e.target.value)}
                  >
                    <option value="">Seleccione estado</option>
                    {estados.map(estado => (
                      <option key={estado.value} value={estado.value}>{estado.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">
                    Código Postal
                    {isLoadingZipCode && <span style={{ marginLeft: '8px', fontSize: '0.85em', color: '#666' }}>(Buscando...)</span>}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={cliente.businessPostalCode || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange('businessPostalCode', value);

                      // Buscar automáticamente cuando tenga 5 dígitos
                      if (value && value.length === 5) {
                        handleZipCodeLookup(value, 'business');
                      }
                    }}
                    placeholder="Código postal"
                    maxLength="5"
                  />
                </div>
                
                <div className="form-group">
                  <label className="form-label">País</label>
                  <select
                    className="form-input"
                    value={cliente.businessCountry || 'Estados Unidos'}
                    onChange={(e) => handleInputChange('businessCountry', e.target.value)}
                  >
                    {paises.map(pais => (
                      <option key={pais.value} value={pais.value}>{pais.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Sección: Dirección de Entrega */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Dirección de Entrega</h3>
              </div>

              <div className="form-group">
                <div className="form-radio-group">
                  <label className="form-radio">
                    <input
                      type="radio"
                      name="tipoEntrega"
                      value="misma"
                      checked={cliente.sameDeliveryAddress !== false}
                      onChange={(e) => handleInputChange('sameDeliveryAddress', true)}
                    />
                    <span className="radio-custom"></span>
                    MISMA DIRECCIÓN
                  </label>

                  <label className="form-radio">
                    <input
                      type="radio"
                      name="tipoEntrega"
                      value="otra"
                      checked={cliente.sameDeliveryAddress === false}
                      onChange={(e) => handleInputChange('sameDeliveryAddress', false)}
                    />
                    <span className="radio-custom"></span>
                    OTRA DIRECCIÓN
                  </label>
                </div>
              </div>

              {/* Botón toggle para dirección residencial */}
              <div className="form-group">
                <label className="form-label">Tipo de dirección de entrega</label>
                <div className="form-radio-group">
                  <label className="form-radio">
                    <input
                      type="radio"
                      name="tipoResidencial"
                      value="comercial"
                      checked={cliente.isResidential !== true}
                      onChange={(e) => handleInputChange('isResidential', false)}
                    />
                    <span className="radio-custom"></span>
                    COMERCIAL
                  </label>

                  <label className="form-radio">
                    <input
                      type="radio"
                      name="tipoResidencial"
                      value="residencial"
                      checked={cliente.isResidential === true}
                      onChange={(e) => handleInputChange('isResidential', true)}
                    />
                    <span className="radio-custom"></span>
                    RESIDENCIAL
                  </label>
                </div>
              </div>

              {cliente.sameDeliveryAddress === false && (
                <>
                  <div className="form-group">
                    <label className="form-label">Número y Calle</label>
                    <input
                      type="text"
                      className="form-input"
                      value={cliente.deliveryAddress || ''}
                      onChange={(e) => handleInputChange('deliveryAddress', e.target.value)}
                      placeholder="Número y calle"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Unidad (Apartamento, Suite, etc.)</label>
                    <input
                      type="text"
                      className="form-input"
                      value={cliente.deliveryUnit || ''}
                      onChange={(e) => handleInputChange('deliveryUnit', e.target.value)}
                      placeholder="Apt, Suite, Unidad, etc. (opcional)"
                    />
                  </div>

                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="form-label">Ciudad</label>
                      <input
                        type="text"
                        className="form-input"
                        value={cliente.deliveryCity || ''}
                        onChange={(e) => handleInputChange('deliveryCity', e.target.value)}
                        placeholder="Ciudad"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">Estado</label>
                      <select
                        className="form-input"
                        value={cliente.deliveryState || ''}
                        onChange={(e) => handleInputChange('deliveryState', e.target.value)}
                      >
                        <option value="">Seleccione estado</option>
                        {estados.map(estado => (
                          <option key={estado.value} value={estado.value}>{estado.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-grid-2">
                    <div className="form-group">
                      <label className="form-label">
                        Código Postal
                        {isLoadingZipCode && <span style={{ marginLeft: '8px', fontSize: '0.85em', color: '#666' }}>(Buscando...)</span>}
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        value={cliente.deliveryPostalCode || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleInputChange('deliveryPostalCode', value);

                          // Buscar automáticamente cuando tenga 5 dígitos
                          if (value && value.length === 5) {
                            handleZipCodeLookup(value, 'delivery');
                          }
                        }}
                        placeholder="Código postal"
                        maxLength="5"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label className="form-label">País</label>
                      <select
                        className="form-input"
                        value={cliente.deliveryCountry || 'Estados Unidos'}
                        onChange={(e) => handleInputChange('deliveryCountry', e.target.value)}
                      >
                        {paises.map(pais => (
                          <option key={pais.value} value={pais.value}>{pais.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Sección: Tipos de Envíos */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Tipos de Envíos</h3>
              </div>
              
              <div className="form-group">
                <select
                  className="form-input"
                  value={cliente.shippingType || ''}
                  onChange={(e) => handleInputChange('shippingType', e.target.value)}
                >
                  <option value="">Seleccione tipo de envío</option>
                  {tiposEnvio.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Columna Derecha */}
          <div className="form-column">
            {/* Sección: Información de Contacto */}
            <div className="form-section">
              <div className="section-header">
                <FiUser className="section-icon" />
                <h2 className="section-title">Contacto</h2>
              </div>
              
              <div className="form-group">
                <label className="form-label required">Nombre Completo</label>
                <input
                  type="text"
                  className={`form-input ${validationErrors.fullName ? 'error' : ''}`}
                  value={cliente.fullName || ''}
                  onChange={(e) => handleInputChange('fullName', e.target.value)}
                  placeholder="Nombre completo de la persona de contacto"
                  maxLength={255}
                />
                {validationErrors.fullName && (
                  <span className="form-error">{validationErrors.fullName}</span>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Teléfonos</label>
                {(cliente.phones && cliente.phones.length > 0) ? (
                  <>
                    {cliente.phones.map((phone, index) => (
                      <div key={index} className="phone-input-group" style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                        <select
                          className="form-input"
                          value={phone.type || 'mobile'}
                          onChange={(e) => {
                            const newPhones = [...cliente.phones];
                            newPhones[index].type = e.target.value;
                            updateClienteData('phones', newPhones);
                          }}
                          style={{ width: '120px' }}
                        >
                          <option value="mobile">Móvil</option>
                          <option value="office">Oficina</option>
                          <option value="home">Casa</option>
                          <option value="fax">Fax</option>
                          <option value="other">Otro</option>
                        </select>
                        <input
                          type="tel"
                          className="form-input"
                          value={phone.number || ''}
                          onChange={(e) => {
                            const newPhones = [...cliente.phones];
                            newPhones[index].number = e.target.value;
                            updateClienteData('phones', newPhones);
                          }}
                          placeholder="(000) 000-0000"
                          style={{ flex: 1 }}
                        />
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={() => {
                            const newPhones = cliente.phones.filter((_, i) => i !== index);
                            // Si eliminamos el principal, hacer principal al primero
                            if (phone.is_primary && newPhones.length > 0) {
                              newPhones[0].is_primary = true;
                            }
                            updateClienteData('phones', newPhones.length > 0 ? newPhones : null);
                          }}
                          style={{ padding: '8px 12px', minWidth: 'auto' }}
                          title="Eliminar teléfono"
                        >
                          <FiX />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn--outline btn--sm"
                      onClick={() => {
                        const newPhones = cliente.phones || [];
                        newPhones.push({
                          number: '',
                          type: 'mobile',
                          is_primary: newPhones.length === 0
                        });
                        updateClienteData('phones', newPhones);
                      }}
                      style={{ marginTop: '8px' }}
                    >
                      + Agregar Teléfono
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn--outline"
                    onClick={() => {
                      updateClienteData('phones', [{
                        number: '',
                        type: 'mobile',
                        is_primary: true
                      }]);
                    }}
                  >
                    + Agregar Teléfono
                  </button>
                )}
                {validationErrors.phones && (
                  <span className="form-error">{validationErrors.phones}</span>
                )}
              </div>
              <div className="form-group">
                <label className="form-label required">Email</label>
                <input
                  type="email"
                  className={`form-input ${validationErrors.email ? 'error' : ''}`}
                  value={cliente.email || ''}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="correo@ejemplo.com"
                />
                {validationErrors.email && (
                  <span className="form-error">{validationErrors.email}</span>
                )}
              </div>
            </div>
            {/* Sección: Impuestos - OCULTA - Valor por defecto: NO (false) */}
            <div className="form-section" style={{ display: 'none' }}>
              <div className="section-header">
                <h3 className="section-title">Impuestos</h3>
              </div>

              <div className="form-group">
                <div className="form-radio-group">
                  <label className="form-radio">
                    <input
                      type="radio"
                      name="impuestos"
                      value="no"
                      checked={!cliente.requiresTaxes}
                      onChange={(e) => handleInputChange('requiresTaxes', false)}
                    />
                    <span className="radio-custom"></span>
                    NO
                  </label>

                  <label className="form-radio">
                    <input
                      type="radio"
                      name="impuestos"
                      value="si"
                      checked={cliente.requiresTaxes === true}
                      onChange={(e) => handleInputChange('requiresTaxes', true)}
                    />
                    <span className="radio-custom"></span>
                    SI
                  </label>
                </div>
              </div>
            </div>

            {/* Sección: Tipos de Pago */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Tipos de Pago</h3>
              </div>
              
              <div className="form-group">
                <select
                  className={`form-input ${validationErrors.paymentType ? 'error' : ''}`}
                  value={cliente.paymentType || 'TERMINOS'}
                  onChange={(e) => handleInputChange('paymentType', e.target.value)}
                >
                  {tiposPago.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                  ))}
                </select>
                {validationErrors.paymentType && (
                  <span className="form-error">{validationErrors.paymentType}</span>
                )}
              </div>
              
              {cliente.paymentType === 'TERMINOS' && (
                <>
                  <div className="form-group">
                    <label className="form-label required">Límite de Crédito ($)</label>
                    <input
                      type="number"
                      className={`form-input ${validationErrors.creditLimit ? 'error' : ''}`}
                      value={cliente.creditLimit || 1000} // Valor mínimo por defecto
                      onChange={(e) => handleInputChange('creditLimit', e.target.value)}
                      placeholder="1000"
                      min="1"
                      step="0.01"
                    />
                    {validationErrors.creditLimit && (
                      <span className="form-error">{validationErrors.creditLimit}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label className="form-label required">Días de Pago</label>
                    <input
                      type="number"
                      className={`form-input ${validationErrors.paymentTerms ? 'error' : ''}`}
                      value={cliente.paymentTerms || ''}
                      onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
                      placeholder="15, 30, 60"
                      min="1"
                    />
                    {validationErrors.paymentTerms && (
                      <span className="form-error">{validationErrors.paymentTerms}</span>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Sección: Tipo de Cliente */}
            <div className="form-section">
              <div className="section-header">
                <h3 className="section-title">Tipo de Cliente</h3>
              </div>
              
              <div className="form-group">
                <label className="form-label required">Tipo de Negocio</label>
                <select
                  className="form-input"
                  value={cliente.businessType}
                  onChange={(e) => updateClienteData('businessType', e.target.value)}
                >
                  <option value="">Seleccione tipo de negocio</option>
                  {tiposCliente.map(tipo => (
                    <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
                  ))}
                </select>
                {validationErrors.businessType && (
                  <span className="form-error">{validationErrors.businessType}</span>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Categoría Cliente</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                  <select
                    className={`form-input ${validationErrors.clientCategory ? 'error' : ''}`}
                    value={cliente.clientCategory || ''}
                    onChange={(e) => handleInputChange('clientCategory', e.target.value)}
                    disabled={isLoadingCategories}
                    style={{ flex: 1 }}
                  >
                    <option value="">
                      {isLoadingCategories ? 'Cargando categorías...' : 'Seleccione una categoría'}
                    </option>
                    {categorias.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn-add-category"
                    onClick={() => setShowCreateCategoryModal(true)}
                    title="Crear nueva categoría"
                    style={{
                      width: '38px',
                      height: '40px',
                      borderRadius: '50%',
                      background: '#7B2D8E',
                      color: 'white',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      flexShrink: 0,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = '#5e2270'}
                    onMouseOut={(e) => e.currentTarget.style.background = '#7B2D8E'}
                  >
                    <FiPlus size={18} />
                  </button>
                  <button
                    type="button"
                    className="btn-delete-category"
                    onClick={() => setShowDeleteCategoryModal(true)}
                    title="Eliminar categoría seleccionada"
                    disabled={!cliente.clientCategory}
                    style={{
                      width: '38px',
                      height: '40px',
                      borderRadius: '50%',
                      background: cliente.clientCategory ? '#dc3545' : '#cccccc',
                      color: 'white',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: cliente.clientCategory ? 'pointer' : 'not-allowed',
                      flexShrink: 0,
                      transition: 'all 0.2s ease',
                      opacity: cliente.clientCategory ? 1 : 0.6
                    }}
                    onMouseOver={(e) => {
                      if (cliente.clientCategory) {
                        e.currentTarget.style.background = '#c82333';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (cliente.clientCategory) {
                        e.currentTarget.style.background = '#dc3545';
                      }
                    }}
                  >
                    <FiMinus size={18} />
                  </button>
                </div>
                {validationErrors.clientCategory && (
                  <span className="form-error">{validationErrors.clientCategory}</span>
                )}
              </div>
              <div className="form-group">
                <label className="form-label required">Vendedor asignado</label>
                <select
                  className={`form-input ${validationErrors.assignedUserId ? 'error' : ''}`}
                  value={cliente.assignedUserId || ''}
                  onChange={(e) => handleInputChange('assignedUserId', e.target.value)}
                >
                  <option value="">Sin asignar</option>
                  {vendedores.map(vendedor => (
                    <option key={vendedor.value} value={vendedor.value}>{vendedor.label}</option>
                  ))}
                </select>
                {validationErrors.assignedUserId && (
                  <span className="form-error">{validationErrors.assignedUserId}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Modal de confirmación para cambios no guardados */}
      <ConfirmModal
        isOpen={showUnsavedChangesModal}
        onCancel={() => setShowUnsavedChangesModal(false)}
        onConfirm={confirmCancel}
        title="¿Descartar cambios?"
        message="Tienes cambios sin guardar. ¿Estás seguro de que quieres salir sin guardar?"
        confirmText="Sí, descartar"
        cancelText="Continuar editando"
      />

      {/* Modal para crear nueva categoría */}
      <CreateCategoryModal
        isOpen={showCreateCategoryModal}
        onClose={() => setShowCreateCategoryModal(false)}
        onSave={handleCreateCategory}
        isLoading={false}
      />

      {/* Modal para confirmar eliminación de categoría */}
      <ConfirmModal
        isOpen={showDeleteCategoryModal}
        onConfirm={handleDeleteCategory}
        onCancel={() => setShowDeleteCategoryModal(false)}
        title="¿Eliminar categoría?"
        message={`¿Estás seguro de que deseas eliminar la categoría "${categorias.find(c => c.value === cliente.clientCategory)?.label || ''}"? Esta acción no se puede deshacer.`}
        confirmText="Sí, eliminar"
        cancelText="Cancelar"
      />
    </div>
  );
};

export default ClienteForm;